import { forwardRef, useCallback } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Text, Truncated } from '@frontend/design-system';

type ChartCTAProps = {
  buttonLabel: string;
  description: string;
  highlight: string;
  title: string;
} & (
  | {
      ctaType: 'sendReminders';
      onCTAClick?: never;
    }
  | {
      ctaType?: never;
      onCTAClick?: () => void;
    }
);

const maxCTAButtonWidth = 128;

export const ChartCTA = forwardRef<HTMLDivElement, ChartCTAProps>(
  ({ buttonLabel, ctaType, description, highlight, onCTAClick, title }: ChartCTAProps, ref) => {
    const { navigate } = useSettingsNavigate();

    const handleCTAClick = useCallback(() => {
      if (ctaType === 'sendReminders') {
        navigate({
          to: '/messaging/auto-messages/:ruleId/editor',
          params: {
            ruleId: 'new',
          },
        });
      } else if (onCTAClick) {
        onCTAClick();
      }
    }, [ctaType, navigate, onCTAClick]);

    return (
      <div css={styles.wrapper} ref={ref}>
        <Icon color='warn' css={styles.icon} name='bulb-small' size={24} />
        <div css={styles.mainContent}>
          <Truncated css={styles.titleWrapper}>
            {title} | <strong>{highlight}</strong>
          </Truncated>
          <Text color='subdued' size='small'>
            {description}
          </Text>
        </div>

        <Button css={styles.ctaButton} onClick={handleCTAClick} variant='tertiary'>
          {buttonLabel}
        </Button>
      </div>
    );
  }
);

ChartCTA.displayName = 'ChartCTA';

const styles = {
  wrapper: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
    margin-top: ${theme.spacing(2)};
  `,

  icon: css`
    color: ${theme.colors.tangerine40};
  `,

  titleWrapper: css`
    font-size: ${theme.font.size.medium};

    > * {
      font-size: inherit;
    }
  `,

  ctaButton: css`
    max-width: ${maxCTAButtonWidth}px;

    > span {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }
  `,

  mainContent: css`
    flex: 1;
    margin-right: ${theme.spacing(1)};
    max-width: calc(100% - ${maxCTAButtonWidth}px - ${theme.spacing(6)});
  `,
};
